import apiService from '@/services/apiService';
import printAgentService from '@/services/printAgentService';
import localPrintSettingsService from '@/services/localPrintSettingsService';

function decodeBase64ToBytes(base64) {
  const binary = window.atob(base64 || '');
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

async function ensurePrintAgentAvailable() {
  try {
    await printAgentService.getHealth();
  } catch (error) {
    const nextError = new Error('El agente local de impresión no está disponible en http://127.0.0.1:9977.');
    nextError.code = 'print_agent_unavailable';
    nextError.cause = error;
    throw nextError;
  }
}

function normalizePrintError(error, settings = {}) {
  const apiError = error?.payload?.error || error?.code || '';
  const backend = settings?.backend || 'raw';
  const printerName = settings?.printerName || '';

  if (apiError === 'backend_down') {
    const suffix = printerName
      ? ` Verifica la impresora "${printerName}" y el backend "${backend}".`
      : ` Verifica la impresora configurada en el agente local y el backend "${backend}".`;
    const nextError = new Error(`El agente local respondió pero no pudo enviar el trabajo a la impresora.${suffix}`);
    nextError.code = 'print_backend_down';
    nextError.cause = error;
    throw nextError;
  }

  if (apiError === 'printer_not_found') {
    const nextError = new Error(
      printerName
        ? `La impresora local "${printerName}" no está disponible en el agente.`
        : 'No hay una impresora local válida configurada para imprimir.'
    );
    nextError.code = 'printer_not_found';
    nextError.cause = error;
    throw nextError;
  }

  if (apiError === 'duplicate_job') {
    return { ok: true, duplicate: true };
  }

  throw error;
}

async function fetchMovementPrintData({ movementId, printMode = 'raw' }) {
  const response = await apiService.get(`/api/cashbox/movements/${movementId}/print-data`, {
    print_mode: printMode || undefined
  });
  return response?.data?.data || null;
}

async function printMovementReceipt({
  movementId,
  settings = null
}) {
  const resolvedSettings = localPrintSettingsService.sanitizeSettings(
    settings || localPrintSettingsService.getSettings()
  );

  await ensurePrintAgentAvailable();

  const preferredMode = resolvedSettings.backend === 'raw' ? 'raw' : 'json';
  const printData = await fetchMovementPrintData({
    movementId,
    printMode: preferredMode
  });
  const printJob = printData?.print_job;
  if (!printJob) {
    throw new Error('No se pudo obtener el contenido imprimible del movimiento.');
  }

  const backend = printJob.backend || resolvedSettings.backend;
  const rawPayloadBase64 = printJob.payload_base64 || printJob.payloadBase64 || '';
  const rawContentType = printJob.content_type || printJob.contentType || 'application/octet-stream';

  try {
    if (printJob.mode === 'raw') {
      return await printAgentService.printBytes({
        bytes: decodeBase64ToBytes(rawPayloadBase64),
        contentType: rawContentType,
        printerName: resolvedSettings.printerName,
        backend,
        copies: resolvedSettings.copies,
        cut: printJob.cut ?? resolvedSettings.cut,
        apiKey: resolvedSettings.apiKey,
        idempotencyKey: printData?.validity?.idempotency_key || printAgentService.buildIdempotencyKey(`cashbox-${movementId}`)
      });
    }

    return await printAgentService.printJson({
      payload: printJob.payload,
      printerName: resolvedSettings.printerName,
      backend,
      copies: resolvedSettings.copies,
      cut: printJob.payload?.cut ?? resolvedSettings.cut,
      apiKey: resolvedSettings.apiKey,
      idempotencyKey: printData?.validity?.idempotency_key || printAgentService.buildIdempotencyKey(`cashbox-${movementId}`)
    });
  } catch (error) {
    return normalizePrintError(error, {
      ...resolvedSettings,
      backend
    });
  }
}

export default {
  fetchMovementPrintData,
  printMovementReceipt
};
